// /var/www/oszczedzam/client/src/components/reports/TrendsChart.js
import React, { useState, useEffect } from 'react';
import { 
  Box, Paper, Typography, Grid, FormControl, InputLabel, 
  Select, MenuItem, Card, CardContent, Divider, Alert
} from '@mui/material';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, 
  ResponsiveContainer, AreaChart, Area, BarChart, Bar
} from 'recharts';
import { useTheme, alpha } from '@mui/material/styles';

function TrendsChart({ transactions, filters }) {
  const theme = useTheme();
  const [chartType, setChartType] = useState('balance');
  const [timeScale, setTimeScale] = useState('monthly');
  const [trendsData, setTrendsData] = useState([]);
  const [trendSummary, setTrendSummary] = useState({
    hasEnoughData: false,
    balanceTrend: 0,
    incomeTrend: 0,
    expenseTrend: 0,
    firstPeriod: null,
    lastPeriod: null
  });

  // Przygotuj dane trendów na podstawie transakcji
  useEffect(() => {
    if (transactions && transactions.length > 0) {
      generateTrendsData();
    }
  }, [transactions, timeScale, filters]);

  // Generuj dane trendów
  const generateTrendsData = () => {
    // Znajdź najstarszą i najnowszą transakcję
    const sortedTransactions = [...transactions].sort((a, b) => 
      new Date(a.date) - new Date(b.date)
    );
    
    if (sortedTransactions.length < 2) {
      setTrendsData([]);
      setTrendSummary({
        hasEnoughData: false,
        balanceTrend: 0,
        incomeTrend: 0,
        expenseTrend: 0,
        firstPeriod: null,
        lastPeriod: null
      });
      return;
    }
    
    const oldestDate = new Date(sortedTransactions[0].date);
    const newestDate = new Date(sortedTransactions[sortedTransactions.length - 1].date);
    
    // Określ zakres dat
    const dateRange = getDateRange(oldestDate, newestDate, timeScale);
    
    if (dateRange.length < 2) {
      setTrendsData([]);
      setTrendSummary({
        hasEnoughData: false,
        balanceTrend: 0,
        incomeTrend: 0,
        expenseTrend: 0,
        firstPeriod: null,
        lastPeriod: null
      });
      return;
    }
    
    // Przygotuj dane według wybranej skali czasu
    const data = dateRange.map(period => {
      const { startDate, endDate, periodName } = period;
      
      // Filtruj transakcje dla danego okresu
      const periodTransactions = transactions.filter(transaction => {
        const transactionDate = new Date(transaction.date);
        return transactionDate >= startDate && transactionDate <= endDate;
      });
      
      // Oblicz sumy dla przychodów i wydatków
      const income = periodTransactions
        .filter(t => t.amount > 0)
        .reduce((sum, t) => sum + t.amount, 0);
      
      const expenses = periodTransactions
        .filter(t => t.amount < 0)
        .reduce((sum, t) => sum + Math.abs(t.amount), 0);
      
      const balance = income - expenses;
      
      return {
        name: periodName,
        przychody: income,
        wydatki: expenses,
        saldo: balance,
        startDate,
        endDate
      };
    });
    
    setTrendsData(data);
    
    // Oblicz trendy dla salda, przychodów i wydatków
    calculateTrends(data);
  };

  // Wygeneruj zakres dat dla analizy trendów
  const getDateRange = (startDate, endDate, scale) => {
    const periods = [];
    
    if (scale === 'monthly') {
      // Zakres miesięczny
      let currentDate = new Date(startDate);
      currentDate.setDate(1); // Ustaw na pierwszy dzień miesiąca
      
      while (currentDate <= endDate) {
        const periodStartDate = new Date(currentDate);
        const periodEndDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
        
        const periodName = formatMonthYear(currentDate);
        
        periods.push({
          startDate: periodStartDate,
          endDate: periodEndDate,
          periodName
        });
        
        // Przejdź do kolejnego miesiąca
        currentDate.setMonth(currentDate.getMonth() + 1);
      }
    } else if (scale === 'quarterly') {
      // Zakres kwartalny
      let currentDate = new Date(startDate);
      currentDate.setDate(1);
      currentDate.setMonth(Math.floor(currentDate.getMonth() / 3) * 3); // Ustaw na początek kwartału
      
      while (currentDate <= endDate) {
        const periodStartDate = new Date(currentDate);
        const periodEndDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 3, 0);
        
        const quarter = Math.floor(currentDate.getMonth() / 3) + 1;
        const periodName = `Q${quarter} ${currentDate.getFullYear()}`;
        
        periods.push({
          startDate: periodStartDate,
          endDate: periodEndDate,
          periodName
        });
        
        // Przejdź do kolejnego kwartału
        currentDate.setMonth(currentDate.getMonth() + 3);
      }
    } else if (scale === 'yearly') {
      // Zakres roczny
      let currentDate = new Date(startDate);
      currentDate.setMonth(0);
      currentDate.setDate(1);
      
      while (currentDate <= endDate) {
        const periodStartDate = new Date(currentDate);
        const periodEndDate = new Date(currentDate.getFullYear(), 11, 31);
        
        const periodName = currentDate.getFullYear().toString();
        
        periods.push({
          startDate: periodStartDate,
          endDate: periodEndDate,
          periodName
        });
        
        // Przejdź do kolejnego roku
        currentDate.setFullYear(currentDate.getFullYear() + 1);
      }
    }
    
    return periods;
  };

  // Formatuj nazwę miesiąca i roku
  const formatMonthYear = (date) => {
    const months = ['Sty', 'Lut', 'Mar', 'Kwi', 'Maj', 'Cze', 'Lip', 'Sie', 'Wrz', 'Paź', 'Lis', 'Gru'];
    return `${months[date.getMonth()]} ${date.getFullYear()}`;
  };

  // Oblicz trendy
  const calculateTrends = (data) => {
    if (data.length < 2) {
      setTrendSummary({
        hasEnoughData: false,
        balanceTrend: 0,
        incomeTrend: 0,
        expenseTrend: 0,
        firstPeriod: null,
        lastPeriod: null
      });
      return;
    }
    
    // Oblicz trendy dla salda, przychodów i wydatków
    const balanceValues = data.map(d => d.saldo);
    const incomeValues = data.map(d => d.przychody);
    const expenseValues = data.map(d => d.wydatki);
    
    // Oblicz średnią zmianę (trend)
    const balanceTrend = calculateAverageTrend(balanceValues);
    const incomeTrend = calculateAverageTrend(incomeValues);
    const expenseTrend = calculateAverageTrend(expenseValues);
    
    setTrendSummary({
      hasEnoughData: true,
      balanceTrend,
      incomeTrend,
      expenseTrend,
      firstPeriod: data[0].name,
      lastPeriod: data[data.length - 1].name
    });
  };
  
  // Oblicz średnią zmianę procentową
  const calculateAverageTrend = (values) => {
    if (values.length < 2) return 0;
    
    let sumOfChanges = 0;
    let countOfChanges = 0;
    
    for (let i = 1; i < values.length; i++) {
      const previous = values[i - 1];
      const current = values[i];
      
      if (previous !== 0) {
        const percentChange = ((current - previous) / Math.abs(previous)) * 100;
        sumOfChanges += percentChange;
        countOfChanges++;
      }
    }
    
    return countOfChanges > 0 ? sumOfChanges / countOfChanges : 0;
  };
  
  // Obsługa zmiany typu wykresu
  const handleChartTypeChange = (event) => {
    setChartType(event.target.value);
  };
  
  // Obsługa zmiany skali czasu
  const handleTimeScaleChange = (event) => {
    setTimeScale(event.target.value);
  };
  
  // Formatuj kwotę
  const formatAmount = (amount) => {
    return amount.toLocaleString('pl-PL', { style: 'currency', currency: 'PLN' });
  };
  
  // Custom tooltip dla wykresu
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Paper elevation={3} sx={{ p: 2, backgroundColor: 'white' }}>
          <Typography variant="subtitle2">{label}</Typography>
          {payload.map((entry, index) => (
            <Box key={`item-${index}`} sx={{ color: entry.color, display: 'flex', alignItems: 'center', my: 0.5 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: entry.color, mr: 1 }} />
              <Typography variant="body2">
                {entry.name}: {formatAmount(entry.value)}
              </Typography>
            </Box>
          ))}
        </Paper>
      );
    }
    return null;
  };
  
  // Określ kolor trendu
  const getTrendColor = (trend) => {
    if (trend > 0) return theme.palette.success.main;
    if (trend < 0) return theme.palette.error.main;
    return theme.palette.text.primary;
  };
  
  // Generuj opis trendu
  const getTrendDescription = (trend, type) => {
    // Dostosuj komunikat na podstawie typu danych i kierunku trendu
    if (type === 'balance') {
      if (trend > 5) return 'Twoje saldo znacząco rośnie. Świetna robota!';
      if (trend > 0) return 'Twoje saldo powoli rośnie. Dobry kierunek!';
      if (trend < -5) return 'Twoje saldo szybko maleje. Zwróć uwagę na wydatki.';
      if (trend < 0) return 'Twoje saldo maleje. Warto przyjrzeć się budżetowi.';
      return 'Twoje saldo utrzymuje się na stabilnym poziomie.';
    } else if (type === 'income') {
      if (trend > 5) return 'Twoje przychody szybko rosną. Świetnie!';
      if (trend > 0) return 'Twoje przychody powoli rosną.';
      if (trend < -5) return 'Twoje przychody znacząco maleją. Warto to sprawdzić.';
      if (trend < 0) return 'Twoje przychody maleją.';
      return 'Twoje przychody są stabilne.';
    } else if (type === 'expense') {
      if (trend > 5) return 'Twoje wydatki szybko rosną. Zastanów się nad ograniczeniem.';
      if (trend > 0) return 'Twoje wydatki rosną. Zwróć na to uwagę.';
      if (trend < -5) return 'Twoje wydatki znacząco maleją. Świetna oszczędność!';
      if (trend < 0) return 'Twoje wydatki maleją. Dobry kierunek!';
      return 'Twoje wydatki utrzymują się na stabilnym poziomie.';
    }
    return '';
  };

  return (
    <Box>
      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">Trendy finansowe</Typography>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            {/* Wybór skali czasu */}
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel id="time-scale-label">Skala czasu</InputLabel>
              <Select
                labelId="time-scale-label"
                id="time-scale"
                value={timeScale}
                label="Skala czasu"
                onChange={handleTimeScaleChange}
              >
                <MenuItem value="monthly">Miesięcznie</MenuItem>
                <MenuItem value="quarterly">Kwartalnie</MenuItem>
                <MenuItem value="yearly">Rocznie</MenuItem>
              </Select>
            </FormControl>
            
            {/* Wybór typu wykresu */}
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel id="chart-type-label">Typ wykresu</InputLabel>
              <Select
                labelId="chart-type-label"
                id="chart-type"
                value={chartType}
                label="Typ wykresu"
                onChange={handleChartTypeChange}
              >
                <MenuItem value="balance">Saldo</MenuItem>
                <MenuItem value="income-expense">Przychody vs Wydatki</MenuItem>
                <MenuItem value="accumulated">Skumulowane Saldo</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
        
        {/* Karty z podsumowaniem trendów */}
        {trendSummary.hasEnoughData && (
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={4}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                    Trend salda
                  </Typography>
                  <Typography variant="h5" component="div" color={getTrendColor(trendSummary.balanceTrend)}>
                    {trendSummary.balanceTrend > 0 ? '+' : ''}{trendSummary.balanceTrend.toFixed(1)}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {getTrendDescription(trendSummary.balanceTrend, 'balance')}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                    Trend przychodów
                  </Typography>
                  <Typography variant="h5" component="div" color={getTrendColor(trendSummary.incomeTrend)}>
                    {trendSummary.incomeTrend > 0 ? '+' : ''}{trendSummary.incomeTrend.toFixed(1)}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {getTrendDescription(trendSummary.incomeTrend, 'income')}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                    Trend wydatków
                  </Typography>
                  <Typography 
                    variant="h5" 
                    component="div" 
                    // Dla wydatków odwracamy kolorystykę - spadek jest pozytywny
                    color={getTrendColor(-trendSummary.expenseTrend)}
                  >
                    {trendSummary.expenseTrend > 0 ? '+' : ''}{trendSummary.expenseTrend.toFixed(1)}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {getTrendDescription(trendSummary.expenseTrend, 'expense')}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
        
        {/* Wykresy trendów */}
        {trendsData.length > 1 ? (
          <Box sx={{ height: 400 }}>
            <ResponsiveContainer width="100%" height="100%">
              {chartType === 'balance' ? (
                // Wykres salda
                <LineChart 
                  data={trendsData}
                  margin={{ top: 10, right: 30, left: 20, bottom: 30 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.text.secondary, 0.2)} />
                  <XAxis 
                    dataKey="name" 
                    angle={-45} 
                    textAnchor="end" 
                    height={60}
                    interval={0}
                  />
                  <YAxis 
                    tick={{ fill: theme.palette.text.secondary }} 
                    tickFormatter={(value) => new Intl.NumberFormat('pl-PL', { 
                      notation: 'compact',
                      compactDisplay: 'short'
                    }).format(value)}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="saldo" 
                    name="Saldo" 
                    stroke={theme.palette.primary.main}
                    strokeWidth={2}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                    isAnimationActive={true}
                    animationDuration={1000}
                  />
                </LineChart>
              ) : chartType === 'income-expense' ? (
                // Wykres przychodów vs wydatków
                <BarChart 
                  data={trendsData}
                  margin={{ top: 10, right: 30, left: 20, bottom: 30 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.text.secondary, 0.2)} />
                  <XAxis 
                    dataKey="name" 
                    angle={-45} 
                    textAnchor="end" 
                    height={60}
                    interval={0}
                  />
                  <YAxis 
                    tick={{ fill: theme.palette.text.secondary }}
                    tickFormatter={(value) => new Intl.NumberFormat('pl-PL', { 
                      notation: 'compact',
                      compactDisplay: 'short'
                    }).format(value)}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar 
                    dataKey="przychody" 
                    name="Przychody" 
                    fill={theme.palette.success.main} 
                    isAnimationActive={true}
                    animationDuration={1000}
                    barSize={20}
                  />
                  <Bar 
                    dataKey="wydatki" 
                    name="Wydatki" 
                    fill={theme.palette.error.main} 
                    isAnimationActive={true}
                    animationDuration={1000}
                    barSize={20}
                  />
                </BarChart>
              ) : (
                // Wykres skumulowanego salda
                <AreaChart 
                  data={trendsData}
                  margin={{ top: 10, right: 30, left: 20, bottom: 30 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.text.secondary, 0.2)} />
                  <XAxis 
                    dataKey="name" 
                    angle={-45} 
                    textAnchor="end" 
                    height={60}
                    interval={0}
                  />
                  <YAxis 
                    tick={{ fill: theme.palette.text.secondary }}
                    tickFormatter={(value) => new Intl.NumberFormat('pl-PL', { 
                      notation: 'compact',
                      compactDisplay: 'short'
                    }).format(value)}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Area 
                    type="monotone" 
                    dataKey="saldo" 
                    name="Saldo" 
                    stroke={theme.palette.primary.main}
                    fill={alpha(theme.palette.primary.main, 0.2)}
                    isAnimationActive={true}
                    animationDuration={1000}
                  />
                </AreaChart>
              )}
            </ResponsiveContainer>
          </Box>
        ) : (
          <Alert severity="info">
            Potrzebujesz więcej danych z różnych okresów, aby wyświetlić trendy.
          </Alert>
        )}
        
        {trendSummary.hasEnoughData && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
            Dane od {trendSummary.firstPeriod} do {trendSummary.lastPeriod}
          </Typography>
        )}
      </Paper>
    </Box>
  );
}

export default TrendsChart;